import type { UserRole } from '@today/types'
import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { roleAtLeast } from '@/auth/role-order'
import { useViewerRole } from '@/auth/useViewerRole'

type RequireRoleProps =
	| { minRole: UserRole; anyOf?: never; children: ReactNode }
	| { anyOf: UserRole[]; minRole?: never; children: ReactNode }

/**
 * Role-gated route / subtree wrapper.
 *
 * Usage:
 *   <RequireRole minRole={UserRole.ADMIN}>...</RequireRole>
 *   <RequireRole anyOf={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>...</RequireRole>
 *
 * - `minRole` (preferred): ordinal check via `roleAtLeast`.
 *   `VIEWER(0) < USER(1) < ADMIN(2) < SUPER_ADMIN(3)`.
 * - `anyOf`: explicit allow-list for non-hierarchical cases.
 *
 * Behavior:
 *   - While auth is still resolving (`useViewerRole().isLoading === true`),
 *     render a minimal spinner — never make a redirect decision on a
 *     transient VIEWER placeholder.
 *   - Once resolved, viewers below the required role are redirected to
 *     `/dashboard`. Authorized viewers see the children unchanged.
 *   - Authentication is assumed to be already handled upstream by
 *     `<ProtectedRoute>`.
 */
export function RequireRole(props: RequireRoleProps) {
	const { children } = props
	const { role, isLoading } = useViewerRole()

	if (isLoading) {
		return (
			<div className="min-h-[50vh] flex items-center justify-center">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ds-primary" />
			</div>
		)
	}

	const permitted =
		'minRole' in props && props.minRole !== undefined
			? roleAtLeast(role, props.minRole)
			: // anyOf is the only other branch per the discriminated-union prop type.
				// Defense-in-depth: if neither is set (shouldn't happen), fall through to not-permitted.
				props.anyOf?.includes(role) === true

	if (!permitted) {
		return <Navigate to="/dashboard" replace />
	}

	return <>{children}</>
}
